// The file, not the barrel: the header and the off-canvas render UiButton in the
// client graph, and the barrel re-exports the off-canvas itself.
import { BaseLink } from '@/features/navigation/BaseLink';
import { BUTTON_VARIANT_MAP } from '@/lib/ui/tailwind-mapping.constants';
import { cn } from '@/lib/utils/tw-merge';
import type { UiButtonProps, UiButtonSize, UiButtonVariant } from './UiButton.types';
import { resolveButtonVariant } from './UiButton.utils';

const SIZE_CLASSES: Record<UiButtonSize, string> = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg',
};

const VARIANT_CLASSES: Record<UiButtonVariant, string> = {
    ...BUTTON_VARIANT_MAP,
    'primary-outline': 'btn-primary-outline',
};

/**
 * The design's button: a navItem renders through BaseLink, an `href` as a plain
 * anchor, anything else as a `<button>`. The use site sets the layout — size
 * and default weight —, the editor's pick on the navItem sets the weight.
 */
export function UiButton({
    item,
    href,
    variant = 'primary',
    size = 'md',
    className,
    children,
    onClick,
    type = 'button',
    disabled,
}: UiButtonProps) {
    const weight = resolveButtonVariant(item?.variant, variant);
    const classes = cn('btn', VARIANT_CLASSES[weight], SIZE_CLASSES[size], className);

    if (item) {
        return (
            <BaseLink item={item} className={classes} onClick={onClick}>
                {children}
            </BaseLink>
        );
    }

    if (href) {
        return (
            <a href={href} className={classes} onClick={onClick}>
                {children}
            </a>
        );
    }

    return (
        <button type={type} className={classes} onClick={onClick} disabled={disabled}>
            {children}
        </button>
    );
}
